import dayjs from "dayjs";

import { createRequest, prune } from "../../utils/utility";
import { ValidationMessages, AssetTypes } from "common/utils/types";

import { validateRequiredNotDefault, validateNumbersInRange, validateDates, CustomValidationArguments } from "common/utils/validation";

export class Token {
  static readonly minPrice: number = 1;
  static readonly minCap: number = 1;
  static readonly minMin: number = 1;
  static readonly minMax: number = 1;
  static readonly minValue: number = 0;
  static readonly minLTV: number = 0;
  static readonly minInterest: number = 0;

  name: string = Token.default().name;
  files: any = Token.default().files;
  symbol: string = Token.default().symbol;
  company: string = Token.default().company;
  description: string = Token.default().description;
  currency: string = Token.default().currency;
  value: number = Token.default().value;
  asset_type: AssetTypes = Token.default().asset_type;
  LTV: number = Token.default().LTV;
  interest: number = Token.default().interest;
  location: string = Token.default().location;
  cap: number = Token.default().cap;
  price: number = Token.default().price;
  min: number = Token.default().min;
  max: number = Token.default().max;
  start: string = Token.default().start;
  end: string = Token.default().end;

  constructor(obj: any) {
    obj && Object.assign(this, prune(obj));

    this.asset_type = 
      !obj ? Token.default().asset_type :
      typeof obj.asset_type === "string" ? AssetTypes[obj.asset_type] :
      obj.asset_type;
  }

  static readonly addFields = (asset_type: AssetTypes) : Array<string> => {
    let fields = [
      'files',
      'name',
      'symbol',
      'company',
      'description',
      'currency',
      'asset_type',
      'value',
    ];

    switch (asset_type) {
      case AssetTypes.REAL_ESTATE:
        fields.push('location');
        break;
      case AssetTypes.LOAN:
        fields = fields.concat([
          'LTV',
          'interest',
        ]);
        break;
      default:
        break;
    }

    return fields;
  };

  static readonly displayedFields = [ //TODO(Marko): After Token and Property merge delete this and use addFields instead
    'asset_type',
    'name',
    'price',
    'cap',
    'min',
    'max',
    'start',
    'end',
    'symbol',
    'company',
    'LTV',
    'location',
    'interest',
    'value',
    'description',
  ];

  static required(asset_type: AssetTypes): any {
    return {
      files: false,
      name: true,
      symbol: true,
      company: true,
      description: true,
      currency: false,
      asset_type: true,
      LTV: asset_type === AssetTypes.LOAN,
      interest: asset_type === AssetTypes.LOAN,
      location: asset_type === AssetTypes.REAL_ESTATE,
      value: true,
      cap: false,
      price: true,
      min: false,
      max: false,
      start: true,
      end: true,
    } 
  }

  static default(): any {
    return {
      files: null,
      name: "",
      symbol: "",
      company: "",
      description: "",
      currency: "EUR",
      asset_type: "",
      LTV: "",
      interest: "",
      location: "",
      value: "",
      cap: "",
      price: "",
      min: "",
      max: "",
      start: "",
      end: "",
    } 
  }

  validate(keys: Array<string>, messages: any): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      Token.required(this.asset_type),
      this,
      Token.default(),
      keys
    );

    validateNumbersInRange(
      [{
        key: 'price',
        min: Token.minPrice,
      }, {
        key: 'cap',
        min: Token.minCap,
      }, {
        key: 'min',
        min: Token.minMin,
      }, {
        key: 'max',
        min: Token.minMax,
      }, {
        key: 'value',
        min: Token.minValue,
      }, {
        key: 'LTV',
        min: Token.minLTV,
      }, {
        key: 'interest',
        min: Token.minInterest,
      }],
      errors,
      this,
      keys,
      messages[ValidationMessages.Minimum],
      messages[ValidationMessages.Maximum],
    );

    validateDates(new CustomValidationArguments({
      keys: ['start', 'end'],
      errors,
      values: this,
      allowedKeys: keys,
      errorMessage: messages[ValidationMessages.Required]
    }));

    if (!errors.start &&
        !errors.end &&
        shouldValidate('start') && 
        shouldValidate('end') &&
        !dayjs(this.start).isBefore(dayjs(this.end))) {
      errors.start = messages[ValidationMessages.Invalid];
      errors.end = messages[ValidationMessages.Invalid];
    }

    return errors;
  }

  private createRequest(fields: Array<string>): any {
    return createRequest(this, fields);
  }

  addRequest(): any {
    let data: any = this.createRequest(Token.addFields(this.asset_type));

    data.sale = {
      cap: this.cap,
      price: this.price,
      min: this.min,
      max: this.max,
      start: this.start,
      end: this.end,
      currency: this.currency,
    };

    if (dayjs(data.sale.start).isValid()) {
      data.sale.start = dayjs(data.sale.start).toISOString();
    }

    if (dayjs(data.sale.end).isValid()) {
      data.sale.end = dayjs(data.sale.end).toISOString();
    }

    data.asset_type = AssetTypes[data.asset_type];

    return data;
  }
}
